import React from 'react';
import { FormControlLabel, Switch } from '@mui/material';
import { isAnyAdmin } from '@common/domain/auth';
import { config } from '@config';
import { EMPTY_GOAL_CHECKLIST_SNAPSHOT, type GoalChecklistSnapshot } from './goalChecklist';
import { DEMO_GOAL_CHECKLIST_SNAPSHOT } from './goalChecklistDemo';
import { GoalChecklistPanel } from './GoalChecklistPanel';

const VISIBILITY_STORAGE_KEY = 'adminGoalChecklistVisible:v1';

export function readGoalChecklistVisibility(): boolean {
  try {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(VISIBILITY_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

export function persistGoalChecklistVisibility(visible: boolean): void {
  try {
    window.localStorage.setItem(VISIBILITY_STORAGE_KEY, visible ? '1' : '0');
  } catch {
    // Local storage is optional; the in-memory toggle still works for this page load.
  }
}

function isRolloutEnabled(rollout?: string): boolean {
  return rollout !== 'off' && rollout !== 'disabled';
}

export function AdminGoalChecklistVisibilityControl({
  groups,
  visible,
  onVisibleChange,
  showGoalChecklist = config.showGoalChecklist,
  rollout = process.env.REACT_APP_ADMIN_GOAL_CHECKLIST_ROLLOUT,
}: {
  groups?: string[];
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  showGoalChecklist?: boolean;
  rollout?: string;
}) {
  if (showGoalChecklist === false) return null;
  if (!isAnyAdmin(groups)) return null;
  if (!isRolloutEnabled(rollout)) return null;
  return (
    <FormControlLabel
      control={(
        <Switch
          size="small"
          checked={visible}
          onChange={(_event, nextVisible) => onVisibleChange(nextVisible)}
        />
      )}
      label="Show goal progress"
      sx={{ m: 0 }}
    />
  );
}

export function AdminGoalChecklistBar({
  groups,
  liveSnapshot,
  defaultExpanded,
  visible = true,
  showGoalChecklist = config.showGoalChecklist,
  nodeEnv = process.env.NODE_ENV,
  rollout = process.env.REACT_APP_ADMIN_GOAL_CHECKLIST_ROLLOUT,
}: {
  groups?: string[];
  liveSnapshot: GoalChecklistSnapshot | null;
  defaultExpanded: boolean;
  visible?: boolean;
  showGoalChecklist?: boolean;
  nodeEnv?: string;
  rollout?: string;
}) {
  if (showGoalChecklist === false) return null;
  if (!visible) return null;
  if (!isAnyAdmin(groups)) return null;
  if (!isRolloutEnabled(rollout)) return null;

  // Render live snapshot in all environments; fall back to demo fixture in development,
  // or empty snapshot in production when live snapshot is absent.
  const snapshot = liveSnapshot ?? (nodeEnv !== 'production' ? DEMO_GOAL_CHECKLIST_SNAPSHOT : EMPTY_GOAL_CHECKLIST_SNAPSHOT);
  return <GoalChecklistPanel snapshot={snapshot} defaultExpanded={defaultExpanded} />;
}
